import React, { useMemo, useState } from "react";
import { Button, ConfigProvider, Input, Table, Typography, theme } from "antd";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { formattedPlayerName } from "@utils/name_utils";
import { useTableBodyHeight } from "../hooks/useTableBodyHeight";
import { coachFlagSheetFor } from "../mappers/coachTag.mapper";
import { FlagLabel } from "../views/FlagTag";

const { Text } = Typography;

const DATE_COL_WIDTH = 110;
const PLAYER_COL_WIDTH = 220;
const FLAG_COL_WIDTH = 140;

// Read only: the coach flag rows the dialog built (coachFlagRowsFor, filters applied), one per
// player, date and flag. The search here narrows them by player name or number. `filtered` says
// a flag filter is on; `period` ends the empty text ("on this day", "this month"). Download saves
// the rows the table shows as `<fileName>.xlsx`.
export const CoachFlagViewTable = ({ rows, filtered, period, teamName, loading, fileName }) => {
    const [search, setSearch] = useState("");
    const [downloading, setDownloading] = useState(false);
    const [areaRef, bodyHeight] = useTableBodyHeight();
    const { token } = theme.useToken();

    const shownRows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return rows
            .map((row) => ({
                ...row,
                name: formattedPlayerName(teamName, `${row.player.first_name} ${row.player.last_name}`),
            }))
            .filter((row) => `${row.name} #${row.player.number}`.toLowerCase().includes(query));
    }, [rows, teamName, search]);

    // Titles and cells sit in left-aligned blocks: a global `.ant-table-cell` rule centers every
    // cell with !important, which a column's `align` cannot override.
    const columns = useMemo(
        () => [
            {
                title: <div className="stv2-bulk-view-cell">Date</div>,
                key: "date",
                width: DATE_COL_WIDTH,
                render: (_, row) => (
                    // Already "YYYY-MM-DD", the platform's date format.
                    <div className="stv2-bulk-view-cell">{row.date}</div>
                ),
            },
            {
                title: <div className="stv2-bulk-view-cell">Player</div>,
                key: "player",
                width: PLAYER_COL_WIDTH,
                render: (_, row) => (
                    <div className="stv2-bulk-view-cell">
                        <span>
                            {row.name} <Text type="secondary">#{row.player.number}</Text>
                        </span>
                    </div>
                ),
            },
            {
                title: <div className="stv2-bulk-view-cell">Flag</div>,
                key: "flag",
                width: FLAG_COL_WIDTH,
                render: (_, row) => (
                    <div className="stv2-bulk-view-cell">
                        <FlagLabel flag={row.flag} />
                    </div>
                ),
            },
            {
                title: <div className="stv2-bulk-view-cell">Note</div>,
                key: "note",
                // One line per row: a long note ends in an ellipsis, with the full note on hover.
                render: (_, row) => (
                    <div className="stv2-bulk-view-cell">
                        <span className="stv2-bulk-view-note" title={row.note}>
                            {row.note}
                        </span>
                    </div>
                ),
            },
        ],
        []
    );

    const download = async () => {
        setDownloading(true);
        try {
            // Loaded on the first click, so the main bundle does not carry the xlsx writer.
            const { default: writeExcelFile } = await import("write-excel-file/browser");
            const { data, options } = coachFlagSheetFor(shownRows);
            await writeExcelFile(data, options).toFile(`${fileName}.xlsx`);
        } catch (e) {
            message.error("Could not download the table. Try again.");
            throw e; // ponytail: rethrown so Sentry records it as an unhandled rejection
        } finally {
            setDownloading(false);
        }
    };

    const emptyText = search.trim()
        ? "No players match"
        : filtered
          ? `No players have the checked flags ${period}.`
          : `No coach flags ${period}.`;

    return (
        <>
            <div className="stv2-bulk-day-toolbar">
                <Input
                    allowClear
                    size="small"
                    prefix={<SearchOutlined />}
                    placeholder="Search players"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="stv2-bulk-day-search"
                />
                <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={downloading}
                    disabled={shownRows.length === 0}
                    onClick={download}
                    data-attr="status-tracker.bulk-tag.download-xlsx"
                >
                    Download
                </Button>
            </div>
            <div ref={areaRef} className="stv2-bulk-table-area">
                {/* Virtual: a month can hold many rows, as in the multi-day grid. The borders take the
                    theme's darker border color, so they still show on the flag-tinted rows. */}
                <ConfigProvider theme={{ components: { Table: { borderColor: token.colorBorder } } }}>
                    <Table
                        className="stv2-bulk-table stv2-bulk-view-table"
                        columns={columns}
                        dataSource={shownRows}
                        rowKey="key"
                        size="small"
                        bordered
                        virtual
                        loading={loading}
                        pagination={false}
                        locale={{ emptyText }}
                        scroll={{ y: bodyHeight }}
                        // Each row carries its flag's color at 10%, over the theme's own background.
                        onRow={(row) => ({
                            style: { background: `color-mix(in srgb, ${row.flag.color} 10%, transparent)` },
                        })}
                    />
                </ConfigProvider>
            </div>
        </>
    );
};
